import React from 'react';
import { DatePicker, Dropdown, IDropdownOption, SearchBox, Stack } from '@fluentui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowRight,
    faBoxesPacking,
    faCalendarCheck, 
    faCalendarDays, 
    faClock, 
    faDolly,
    faExclamationTriangle, 
    faList, 
    faListCheck,
    faRoute,
    faShip,
    faSpinner, 
    faTableColumns,
    faTruck,
    faTruckFast
} from '@fortawesome/pro-light-svg-icons';
import moment from 'moment';
import styles from '../styles.module.scss';
import { IActiveOrdersRecap, TOperationStatusFilter, TOperationTypeFilter, TOperationViewMode } from '../types';
import GeneralService from '../../../../services/general';
import Label from '../../../typography/label';
import Text from '../../../typography/text';

interface IWorkspaceHeaderProps {
    counts: {
        total: number;
        overdue: number;
        scheduled: number;
        inProgress: number;
        completed: number;
    };
    activeOrdersRecap: IActiveOrdersRecap;
    onOpenOrdersList(): void;
    selectedDate: {
        startDate: string;
        endDate: string;
    };
    onDateChange(startDate: string, endDate: string): void;
    
    statusFilter: TOperationStatusFilter;
    onStatusFilterChange(filter: TOperationStatusFilter): void;
    
    typeFilter: TOperationTypeFilter;
    onTypeFilterChange(filter: TOperationTypeFilter): void;
    
    searchKeyword: string;
    onSearchChange(keyword: string): void;
    
    viewMode: TOperationViewMode;
    onViewModeChange(mode: TOperationViewMode): void;
    
    onRefresh(): void;
}

const TYPE_OPTIONS: IDropdownOption[] = [
    { key: 'all', text: 'All Activity Types' },
    { key: 'ship', text: 'Vessel Schedule (Shipping)' },
    { key: 'loading', text: 'Depot Loading' },
    { key: 'unloading', text: 'Port Unloading' },
    { key: 'dooring', text: 'Dooring (Trucking)' },
];

const WorkspaceHeader: React.FC<IWorkspaceHeaderProps> = ({
    counts,
    activeOrdersRecap,
    onOpenOrdersList,
    selectedDate,
    onDateChange,
    statusFilter,
    onStatusFilterChange,
    typeFilter,
    onTypeFilterChange,
    searchKeyword,
    onSearchChange,
    viewMode,
    onViewModeChange,
    onRefresh,
}) => {
    return (
        <div className={styles.headerContainer}>
            {/* KPI Metrics Summary Bar */}
            <div className={styles.kpiGrid}>
                {/* Total */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'all' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('all')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Total Activities</span>
                        <span className={styles.kpiValue}>{counts.total}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#f1f5f9', color: '#0284c7' }}>
                        <FontAwesomeIcon icon={faCalendarCheck} />
                    </div>
                </div>

                {/* Overdue */}
                <div 
                    className={`${styles.kpiCard} ${styles.kpiOverdue} ${statusFilter === 'overdue' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('overdue')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel} style={{ color: '#b91c1c' }}>Overdue</span>
                        <span className={styles.kpiValue} style={{ color: '#dc2626' }}>{counts.overdue}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#fee2e2', color: '#dc2626' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                </div>

                {/* Scheduled */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'scheduled' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('scheduled')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel} style={{ color: '#6366f1' }}>Scheduled</span>
                        <span className={styles.kpiValue} style={{ color: '#4f46e5' }}>{counts.scheduled}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#e0e7ff', color: '#6366f1' }}>
                        <FontAwesomeIcon icon={faClock} />
                    </div>
                </div>

                {/* In Progress */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'in_progress' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('in_progress')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>In Progress</span>
                        <span className={styles.kpiValue} style={{ color: '#0284c7' }}>{counts.inProgress}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#e0f2fe', color: '#0284c7' }}>
                        <FontAwesomeIcon icon={faSpinner} />
                    </div>
                </div>

                {/* Completed */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'completed' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('completed')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Completed</span>
                        <span className={styles.kpiValue} style={{ color: '#16a34a' }}>{counts.completed}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#dcfce7', color: '#16a34a' }}>
                        <FontAwesomeIcon icon={faCalendarDays} />
                    </div>
                </div>
            </div>

            {/* Controls Bar: Date Navigation, Search, Filters, View Switcher */}
            <div className={styles.controlsBar}>
                {/* Date Range Navigation */}
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <div className={styles.dateRangeContainer}>
                        <span className={styles.dateLabel}>Period:</span>
                        <DatePicker
                            placeholder="Start date..."
                            value={moment(selectedDate.startDate).toDate()}
                            onSelectDate={(date) => {
                                if (date) {
                                    const m = moment(date).startOf('day');
                                    let endM = moment(selectedDate.endDate);
                                    if (endM.isBefore(m)) {
                                        endM = m.clone().endOf('day');
                                    }
                                    onDateChange(m.toISOString(), endM.toISOString());
                                }
                            }}
                            formatDate={GeneralService.formatDate}
                            styles={{ root: { width: 135 } }}
                        />
                        <div className={styles.dateRangeSeparator}>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </div>
                        <DatePicker
                            placeholder="End date..."
                            value={moment(selectedDate.endDate).toDate()}
                            onSelectDate={(date) => {
                                if (date) {
                                    const m = moment(date).endOf('day');
                                    let startM = moment(selectedDate.startDate);
                                    if (m.isBefore(startM)) {
                                        startM = m.clone().startOf('day');
                                    }
                                    onDateChange(startM.toISOString(), m.toISOString());
                                }
                            }}
                            formatDate={GeneralService.formatDate}
                            styles={{ root: { width: 135 } }}
                        />
                    </div>
                </Stack>

                {/* Search & Filters */}
                <div className={styles.filtersGroup}>
                    <div className={styles.searchBoxWrapper}>
                        <SearchBox
                            placeholder="Search No. Order (#1318 / ZX2N), Container, Vessel, Driver..."
                            value={searchKeyword}
                            onChange={(_, val) => onSearchChange(val || '')}
                            onClear={() => onSearchChange('')}
                        />
                    </div>

                    <Dropdown
                        options={TYPE_OPTIONS}
                        selectedKey={typeFilter}
                        onChange={(_, opt) => {
                            if (opt) onTypeFilterChange(opt.key as TOperationTypeFilter);
                        }}
                        styles={{ root: { minWidth: 190 } }}
                    />

                    {/* View Switcher Toggle */}
                    <div className={styles.viewToggleGroup}>
                        <button
                            className={`${styles.viewToggleButton} ${viewMode === 'table' ? styles.viewToggleActive : ''}`}
                            onClick={() => onViewModeChange('table')}
                            title="Operational Table View"
                        >
                            <FontAwesomeIcon icon={faList} /> Table
                        </button>
                        <button
                            className={`${styles.viewToggleButton} ${viewMode === 'board' ? styles.viewToggleActive : ''}`}
                            onClick={() => onViewModeChange('board')}
                            title="Board / Card View"
                        >
                            <FontAwesomeIcon icon={faTableColumns} /> Board
                        </button>
                    </div>
                </div>
            </div>

            {/* Active Running Orders Summary & Breakdown Bar (Minimalist) */}
            <div className={styles.activeOrdersRecapContainer}>
                <div className={styles.activeOrdersLeft}>
                    <div className={styles.activeOrdersIconBadge}>
                        <FontAwesomeIcon icon={faRoute} />
                    </div>
                    <div className={styles.activeOrdersTextGroup}>
                        <span className={styles.activeOrdersTitle}>Running Orders</span>
                        <span className={styles.activeOrdersCountBadge}>
                            {activeOrdersRecap.totalActiveOrders} Active
                        </span>
                        {activeOrdersRecap.totalOrdersInFilter > 0 && (
                            <span className={styles.activeOrdersSubCount}>
                                ({activeOrdersRecap.totalActiveOrders} of {activeOrdersRecap.totalOrdersInFilter} in period)
                            </span>
                        )}
                    </div>
                </div>

                <div className={styles.activeOrdersBreakdownGroup}>
                    {/* Loading */}
                    <div
                        className={`${styles.breakdownPill} ${typeFilter === 'loading' ? styles.breakdownPillActive : ''}`}
                        onClick={() => onTypeFilterChange(typeFilter === 'loading' ? 'all' : 'loading')}
                        title="Filter Depot Loading orders"
                    >
                        <span className={styles.breakdownDot} style={{ backgroundColor: '#f59e0b' }} />
                        <span className={styles.breakdownLabel}>Loading</span>
                        <span className={`${styles.breakdownValue} ${activeOrdersRecap.breakdown.loading === 0 ? styles.breakdownValueMuted : ''}`}>
                            {activeOrdersRecap.breakdown.loading}
                        </span>
                    </div>

                    {/* Sailing */}
                    <div
                        className={`${styles.breakdownPill} ${typeFilter === 'ship' ? styles.breakdownPillActive : ''}`}
                        onClick={() => onTypeFilterChange(typeFilter === 'ship' ? 'all' : 'ship')}
                        title="Filter Vessel Sailing orders"
                    >
                        <span className={styles.breakdownDot} style={{ backgroundColor: '#0d9488' }} />
                        <span className={styles.breakdownLabel}>Sailing</span>
                        <span className={`${styles.breakdownValue} ${activeOrdersRecap.breakdown.sailing === 0 ? styles.breakdownValueMuted : ''}`}>
                            {activeOrdersRecap.breakdown.sailing}
                        </span>
                    </div>

                    {/* Unloading */}
                    <div
                        className={`${styles.breakdownPill} ${typeFilter === 'unloading' ? styles.breakdownPillActive : ''}`}
                        onClick={() => onTypeFilterChange(typeFilter === 'unloading' ? 'all' : 'unloading')}
                        title="Filter Port Unloading orders"
                    >
                        <span className={styles.breakdownDot} style={{ backgroundColor: '#8b5cf6' }} />
                        <span className={styles.breakdownLabel}>Unloading</span>
                        <span className={`${styles.breakdownValue} ${activeOrdersRecap.breakdown.unloading === 0 ? styles.breakdownValueMuted : ''}`}>
                            {activeOrdersRecap.breakdown.unloading}
                        </span>
                    </div>

                    {/* Dooring */}
                    <div
                        className={`${styles.breakdownPill} ${typeFilter === 'dooring' ? styles.breakdownPillActive : ''}`}
                        onClick={() => onTypeFilterChange(typeFilter === 'dooring' ? 'all' : 'dooring')}
                        title="Filter Dooring / Trucking orders"
                    >
                        <span className={styles.breakdownDot} style={{ backgroundColor: '#10b981' }} />
                        <span className={styles.breakdownLabel}>Dooring</span>
                        <span className={`${styles.breakdownValue} ${activeOrdersRecap.breakdown.dooring === 0 ? styles.breakdownValueMuted : ''}`}>
                            {activeOrdersRecap.breakdown.dooring}
                        </span>
                    </div>

                    {/* View Details Button */}
                    <button
                        type="button"
                        className={styles.btnViewOrders}
                        onClick={onOpenOrdersList}
                        title="View list of running orders in detail"
                    >
                        <FontAwesomeIcon icon={faListCheck} style={{ fontSize: 11 }} />
                        <span>View Orders</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WorkspaceHeader;
